import { cloneElement, isValidElement, useId } from 'react';
import type { ReactNode } from 'react';

/**
 * A labelled form field: the label, ONE control, and the note under it — the error when there is one, else the hint.
 *
 * 🔴 THE LABEL NAMES ITS CONTROL WITHOUT THE SCREEN'S HELP — DS-2a (F381, F383, F40). Before, `htmlFor` was the only
 * link, 191 of 363 fields never passed it (the sign-in form among them), and a screen reader announced an unnamed box.
 * The field gives its one control an id (the control's own, when it has one), links the note through
 * `aria-describedby`, marks the control `aria-invalid` on an error and `aria-required` when `required`.
 *
 * DS-5: the error is `role="alert"`, so it is read out when it appears (F381), and `required` shows on the label.
 * The plan's `FieldContext` is this contract instead: the kit's controls say `wbLabellable` and receive the id, the
 * description and the invalid flag as props, which a raw `input`, `select` or `textarea` also understands — one
 * mechanism for both, already covering 363 fields.
 *
 * ⚠️ ONLY A SINGLE CONTROL IS TOUCHED: a field holding two ("from" and "to") is left as the screen built it, because
 * one id cannot name both.
 */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  /** Shown on the label and set as `aria-required` on the control. The form still checks it: this only says so. */
  required?: boolean;
  /**
   * The id of the control this labels. Optional since DS-2a: a field with one labellable control gives it an id
   * itself. Still honoured for a control the field cannot see, such as one wrapped in another element.
   */
  htmlFor?: string;
  children: ReactNode;
}) {
  const generated = useId();
  const control =
    isValidElement<{ id?: string; 'aria-describedby'?: string }>(children) && labellable(children.type) ? children : null;
  const controlId = control ? (control.props.id ?? htmlFor ?? generated) : htmlFor;
  const note = error ?? hint;
  const noteId = `${controlId ?? generated}-note`;
  return (
    <div className="wb-field">
      <label className="wb-label" htmlFor={controlId}>
        {label}
        {required ? <span className="wb-label__required"> (required)</span> : null}
      </label>
      {control
        ? cloneElement(control, {
            id: controlId,
            ...(note ? { 'aria-describedby': [control.props['aria-describedby'], noteId].filter(Boolean).join(' ') } : {}),
            ...(error ? { 'aria-invalid': true } : {}),
            ...(required ? { 'aria-required': true } : {}),
          })
        : children}
      {error ? (
        <span className="wb-error" id={noteId} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="wb-hint" id={noteId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** Whether a `Field` may give this element an id: the plain form controls, and kit controls that say so. */
export function labellable(type: unknown): boolean {
  if (typeof type === 'string') return type === 'input' || type === 'select' || type === 'textarea';
  return Boolean(type && (type as { wbLabellable?: boolean }).wbLabellable);
}
